import { useState, useEffect } from 'react';
import { Table, Button, Space, message, Input, Modal } from 'antd';
import { SearchOutlined, ReloadOutlined, MessageOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

interface UserFeedbackItem {
  id: number;
  user_name: string;
  phone: string;
  email: string | null;
  feedback_topic: string;
  feedback_content: string;
  create_time: string;
}

export default function UserFeedback() {
  const [feedbacks, setFeedbacks] = useState<UserFeedbackItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<UserFeedbackItem | null>(null);

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (searchKeyword) {
        params.append('keyword', searchKeyword);
      }
      
      const resp = await fetch(`/api/admin/user-feedback?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setFeedbacks(resp.data || []);
      } else {
        message.error(resp?.error || '获取数据失败');
      }
    } catch (err) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchFeedbacks();
  };

  // 格式化时间
  const formatDateTime = (time: string | null) => {
    if (!time) return '-';
    return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
  };

  // 打开详情弹窗
  const handleViewDetail = (record: UserFeedbackItem) => {
    setSelectedFeedback(record);
    setDetailModalVisible(true);
  };

  // 关闭详情弹窗
  const handleCloseDetail = () => {
    setDetailModalVisible(false);
    setSelectedFeedback(null);
  };

  const columns: ColumnsType<UserFeedbackItem> = [
    {
      title: '姓名',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 120,
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
    },
    {
      title: '电子邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (text: string | null) => text || '-',
    },
    {
      title: '反馈主题',
      dataIndex: 'feedback_topic',
      key: 'feedback_topic',
      width: 200,
      ellipsis: true,
    },
    {
      title: '详细内容',
      dataIndex: 'feedback_content',
      key: 'feedback_content',
      width: 300,
      ellipsis: true,
      render: (text: string) => (
        <span title={text}>{text}</span>
      ),
    },
    {
      title: '反馈时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 180,
      render: (text: string) => (
        <span style={{ color: '#6b7280', fontSize: 13 }}>{formatDateTime(text)}</span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: UserFeedbackItem) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
          style={{ padding: 0 }}
        >
          查看详情
        </Button>
      ),
    },
  ];

  // 根据搜索关键词过滤数据（前端过滤作为备用方案）
  const filteredData = searchKeyword
    ? feedbacks.filter(
        (item) =>
          item.user_name?.includes(searchKeyword) ||
          item.phone?.includes(searchKeyword) ||
          item.email?.includes(searchKeyword) ||
          item.feedback_topic?.includes(searchKeyword) ||
          item.feedback_content?.includes(searchKeyword)
      )
    : feedbacks;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <MessageOutlined />
          </div>
          <div>
            <h2>信息反馈</h2>
            <p>查看用户反馈信息</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索姓名/电话/邮箱/主题/内容"
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 280, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchFeedbacks}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
        </Space>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 1260 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>

      {/* 详情弹窗 */}
      <Modal
        title="反馈详情"
        open={detailModalVisible}
        onCancel={handleCloseDetail}
        footer={[
          <Button key="close" onClick={handleCloseDetail}>
            关闭
          </Button>,
        ]}
        width={700}
      >
        {selectedFeedback && (
          <div style={{ padding: '8px 0' }}>
            {/* 姓名、联系电话、电子邮箱 - 同一行 */}
            <div style={{ 
              display: 'flex', 
              gap: '24px', 
              marginBottom: '20px',
              flexWrap: 'wrap'
            }}>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>姓名</div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{selectedFeedback.user_name || '-'}</div>
              </div>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>联系电话</div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{selectedFeedback.phone || '-'}</div>
              </div>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>电子邮箱</div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{selectedFeedback.email || '-'}</div>
              </div>
            </div>

            {/* 反馈主题 - 单独一行 */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>反馈主题</div>
              <div style={{ fontSize: '14px', fontWeight: 500 }}>{selectedFeedback.feedback_topic || '-'}</div>
            </div>

            {/* 详细内容 - 单独一行，带滚动条 */}
            <div>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>详细内容</div>
              <div
                style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {selectedFeedback.feedback_content || '-'}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

